import { useEffect } from "react";
import { PortfolioProject } from "@/mocks/portfolioData";

interface ProjectModalProps {
  project: PortfolioProject | null;
  onClose: () => void;
}

export default function ProjectModal({ project, onClose }: ProjectModalProps) {
  useEffect(() => {
    if (!project) return;
    document.body.style.overflow = "hidden";
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [project, onClose]);

  if (!project) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-950/75 backdrop-blur-sm" />

      {/* Panel */}
      <div
        className="relative bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero image */}
        <div className="relative w-full h-52 md:h-64 overflow-hidden rounded-t-2xl flex-shrink-0">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
          {/* Category badge */}
          <div className="absolute top-4 left-4">
            <span className="bg-white/90 backdrop-blur-sm text-slate-700 text-xs font-semibold px-3 py-1 rounded-full">
              {project.category}
            </span>
          </div>
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 text-slate-700 hover:bg-white cursor-pointer transition-colors"
            aria-label="Close"
          >
            <i className="ri-close-line text-lg" />
          </button>
          {/* Title overlay */}
          <div className="absolute bottom-4 left-4 right-4">
            <h2 className="text-xl md:text-2xl font-black text-white leading-tight">{project.title}</h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Meta row */}
          <div className="flex flex-wrap gap-4 mb-6 pb-6 border-b border-slate-100">
            {[
              { icon: "ri-building-line", label: "Client", value: project.client },
              { icon: "ri-briefcase-line", label: "Industry", value: project.industry },
              { icon: "ri-time-line", label: "Duration", value: project.duration },
              { icon: "ri-calendar-line", label: "Year", value: project.year },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-100">
                  <i className={`${item.icon} text-slate-500 text-sm`} />
                </div>
                <div>
                  <div className="text-xs text-slate-400">{item.label}</div>
                  <div className="text-sm font-semibold text-slate-800">{item.value}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-2 mb-6">
            {project.tags.map((tag) => (
              <span key={tag} className="bg-slate-50 border border-slate-200 text-slate-600 text-xs px-2.5 py-1 rounded-full">
                {tag}
              </span>
            ))}
          </div>

          {/* Challenge */}
          <div className="mb-5">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-error-warning-line" style={{color: "#9bc400"}} />
              </div>
              The Challenge
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">{project.challenge}</p>
          </div>

          {/* Solution */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-lightbulb-line" style={{color: "#9bc400"}} />
              </div>
              Our Solution
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed">{project.solution}</p>
          </div>

          {/* Results */}
          <div className="mb-6">
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-bar-chart-2-line" style={{color: "#9bc400"}} />
              </div>
              Results
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {project.results.map((r) => (
                <div key={r.metric} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="text-lg font-black" style={{color: "#9bc400"}}>{r.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{r.metric}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          {project.testimonial && (
            <div className="bg-black rounded-xl p-5">
              <i className="ri-double-quotes-l text-[#C8F135] text-2xl mb-2 block" />
              <p className="text-slate-300 text-sm leading-relaxed mb-4 italic">
                &ldquo;{project.testimonial.text}&rdquo;
              </p>
              <div>
                <div className="text-white text-sm font-bold">{project.testimonial.author}</div>
                <div className="text-slate-400 text-xs">{project.testimonial.role}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
